import React, { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { z } from "zod";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import { SearchSelectMulti } from "../../../components/common/form/SearchSelect/SearchSelectMulti";
import {
  useGetCoursesBySchoolByQueryQuery,
  useGetDegreesBySchoolByQueryQuery,
} from "../../../graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";

const modals = {
  inviteConfirmation: {
    title: "Success!",
    ariaLabel: "Degrees Added Successfully Modal",
    warningText: "Degrees have been added.",
    actionButtonText: "OK",
  },
  inviteError: {
    title: "Error!",
    ariaLabel: "Degrees Added Error Modal",
    warningText: "An error occurred while adding the degrees. Please try again.",
    actionButtonText: "CLOSE",
  },
};

export enum AddDegreesModalState {
  CLOSED = "CLOSED",
  ADD_CONFIRMATION = "ADD_CONFIRMATION",
  ADD_ERROR = "ADD_ERROR",
}

const formSchema = z.object({
  degrees: z.array(z.object({ id: z.string(), name: z.string() })),
});

type FormData = {
  degrees: { id: string; name: string }[];
};

interface AddDegreesModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (degreeIds: string[]) => Promise<boolean>;
  coursesSearchQuery: string;
}

const AddDegreesModalContainer = ({ visible, onClose, onSubmit, coursesSearchQuery }: AddDegreesModalProps) => {
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState(AddDegreesModalState.CLOSED);
  const [searchQuery, setSearchQuery] = useState("");

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const { control, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      degrees: [],
    },
    mode: "onChange",
  });

  const schoolId = appConfig.axioSchools[0];

  const {
    data: degreesData,
    refetch,
    loading,
  } = useGetDegreesBySchoolByQueryQuery({
    variables: {
      schoolId,
      query: searchQuery,
      page: 1,
      pageSize: 10,
    },
  });

  const { refetch: coursesRefetch } = useGetCoursesBySchoolByQueryQuery({
    variables: {
      schoolId,
      query: coursesSearchQuery,
      page: 1,
      pageSize: 10,
    },
  });
  useEffect(() => {
    refetch();
  }, [searchQuery]);

  const handleFormSubmit = async (formData: FormData) => {
    try {
      const degreeIds = formData.degrees.map((c) => c.id);

      const success = await onSubmit(degreeIds);
      if (success) {
        reset({ degrees: [] });
        onClose();
        coursesRefetch();
        setModalState(AddDegreesModalState.ADD_CONFIRMATION);
      } else {
        setModalState(AddDegreesModalState.ADD_ERROR);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState(AddDegreesModalState.ADD_ERROR);
    }
  };

  const handleModalClose = () => {
    setModalState(AddDegreesModalState.CLOSED);
  };

  return (
    <>
      <CustomModal
        visible={visible}
        onRequestClose={onClose}
        title="Assign Degree"
        exitFill="#6B7280"
        ariaLabel="Add degrees modal"
        style={{ width: isMedium ? "50%" : "90%" }}
        onShow={() => {}}
        showCloseButton={true}>
        <View style={styles.container}>
          <View style={styles.formGroup}>
            <SearchSelectMulti
              name="degrees"
              control={control}
              label="Degrees"
              loading={loading}
              placeholder="Start typing to search and select one or more degrees."
              backgroundColor={primaryColor}
              showAllByDefault={true}
              itemType="degree"
              fetchOptions={async (searchQuery) => {
                setSearchQuery(searchQuery);

                return (
                  degreesData?.getDegreesBySchoolByQuery.data.degrees?.map((d) => ({
                    id: d.id,
                    name: d.name,
                  })) ?? []
                );
              }}
            />
          </View>

          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              onPress={onClose}
              styleType="secondary"
              size="m"
              style={styles.cancelButton}
              textStyle={styles.cancelButtonText}
              leftIcon={null}
              rightIcon={null}
              disabled={false}
              textProps={{}}
            />
            <CustomButton
              text="Assign Degree"
              onPress={handleSubmit(handleFormSubmit)}
              styleType="primary"
              size="m"
              style={styles.sendButton}
              textStyle={styles.sendButtonText}
              leftIcon={null}
              rightIcon={null}
              textProps={{}}
            />
          </View>
        </View>
      </CustomModal>
      <NewCustomAxioActionModal
        visible={modalState === AddDegreesModalState.ADD_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.inviteConfirmation}
      />
      <NewCustomAxioActionModal
        visible={modalState === AddDegreesModalState.ADD_ERROR}
        onClose={handleModalClose}
        modalTexts={modals.inviteError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
  },
  row: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 16,
    alignItems: "flex-end",
  },
  formGroup: {
    flex: 1,
    marginBottom: 24,
  },
  inputContainer: {
    minHeight: 70,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  sendButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  sendButtonText: {
    color: "#FFFFFF",
  },
});

export default AddDegreesModalContainer;
